import { createInstance, type i18n as I18nInstance } from "i18next";
import { I18nextProvider, useTranslation } from "react-i18next";
import { useEffect, useMemo, type ReactNode } from "react";
import { RESOURCES } from "./resources";
import type { Lang } from "./config";
import { getUiTranslations, translationFor } from "./ui-translations.functions";

const instances = new Map<Lang, I18nInstance>();

/** En i18next-instans per språk, initierad synkront (alla ordlistor medföljer). */
export function getI18n(lang: Lang): I18nInstance {
  let inst = instances.get(lang);
  if (!inst) {
    inst = createInstance();
    void inst.init({
      lng: lang,
      fallbackLng: "sv",
      resources: RESOURCES,
      react: { useSuspense: false },
      interpolation: { escapeValue: false },
    });
    instances.set(lang, inst);
  }
  return inst;
}

export function I18nProvider({ lang, children }: { lang: Lang; children: ReactNode }) {
  const i18n = useMemo(() => getI18n(lang), [lang]);

  useEffect(() => {
    let active = true;
    void getUiTranslations().then((rows) => {
      if (!active || rows.length === 0) return;
      for (const row of rows) {
        const value = translationFor(row, lang);
        if (value.trim()) i18n.addResource(lang, "translation", row.translation_key, value);
      }
      // addResource uppdaterar resurslagret; språkbytet får alla hooks att rendera om.
      void i18n.changeLanguage(lang);
    });
    return () => {
      active = false;
    };
  }, [i18n, lang]);

  return <I18nextProvider i18n={i18n}>{children}</I18nextProvider>;
}

/** Kortgenväg för att hämta en översättning utan hook (t.ex. i toasts). */
export function tFor(lang: Lang) {
  return getI18n(lang).t.bind(getI18n(lang));
}

export { useTranslation };
export { useLocale } from "./locale-context";
